import {
  Component, inject, signal, computed,
  ElementRef, viewChild, OnInit, ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { RelatorioDisparoService } from '../../shared/service/relatório-disparo.service';
import { RelatorioDisparo } from '../../shared/model/relatorio-disparo/relatorio-disparo.model';
import { RelatorioComprovacaoEnvio } from '../../shared/model/relatorio-disparo/relatorio-comprovacao-envio.model';
import { AuthService } from '../../shared/service/auth.service';
import { CpfCnpjMaskPipe } from '../../shared/pipe/cpf-cnpj-mask.pipe';
import { CelularMaskPipe } from '../../shared/pipe/celular-mask.pipe';
import { UsuarioLogado } from '../../shared/model/usuario/usuario-logado.model';
import { MsgService } from '../../shared/service/msg.service';
import { MatTooltip } from "@angular/material/tooltip";

const ROXO: [number, number, number] = [153, 61, 245];
const GRAFITE: [number, number, number] = [43, 43, 43];
const CINZA: [number, number, number] = [196, 196, 143];

@Component({
  selector: 'app-relatorio-disparo',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltip
],
  templateUrl: './relatorio-disparo.component.html',
  styleUrls: ['./relatorio-disparo.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RelatorioDisparoComponent implements OnInit {

  readonly painelRef = viewChild<ElementRef<HTMLElement>>('painel');
  readonly carregando = signal(false);
  readonly exportando = signal(false);
  readonly relatorio = signal<RelatorioDisparo | null>(null);
  readonly erro = signal<string | null>(null);
  readonly dataExport = signal<Date>(new Date());
  readonly progresso = signal<{ atual: number; total: number } | null>(null);
  readonly temDados = computed(() => this.relatorio() !== null);
  private readonly cpfMask = new CpfCnpjMaskPipe();
  private readonly telMask = new CelularMaskPipe();
  private readonly dialogData  = inject<{ disparoId: number }>(MAT_DIALOG_DATA);
  readonly skLinhas = Array.from({ length: 6 });
  readonly skCards  = Array.from({ length: 4 });
  readonly skRows   = Array.from({ length: 20 });
  readonly ITENS_P1 = 18;
  readonly ITENS_P2 = 27;

  readonly progressoPct = computed(() => {
    const p = this.progresso();
    return p && p.total > 0 ? Math.round((p.atual / p.total) * 100) : 0;
  });

  readonly comprovacao = computed<RelatorioComprovacaoEnvio[]>(
    () => this.relatorio()?.comprovacaoEnvio ?? []
  );

  readonly mensagemDisparo = computed(() => {
    const direta = this.relatorio()?.dadosDisparo.mensagem?.trim();
    if (direta) return direta;
    const tpl = this.relatorio()?.dadosDisparo.template;
    const componentes = tpl?.componentes ?? tpl?.components ?? [];
    const body = componentes.find(c => (c.type ?? '').toUpperCase() === 'BODY');
    return body?.text?.trim() ?? '';
  });

  readonly mensagemHtml = computed(() => {
    const txt = this.mensagemDisparo();
    if (!txt) return '';
    const escapado = txt
      .replace(/\\n/g, '\n')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escapado
      .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_\n]+)_/g, '<em>$1</em>')
      .replace(/\r?\n/g, '<br>');
  });

  private readonly colunasPdf: readonly {head: string; peso: number; center?: boolean; valor: (r: RelatorioComprovacaoEnvio) => string;}[] = [
    { head: 'Nome do Contato', peso: 10,  valor: r => r.nomeContato ?? '—' },
    { head: 'Telefone', peso: 10,  center: true, valor: r => this.formatarTelefone(r.numeroContato) },
    { head: 'CPF', peso: 10,   center: true, valor: r => this.formatarCpf(r.cpfContato) },
    { head: 'ID', peso: 10,  valor: r => r.idMensagem ?? '—' },
    { head: 'META ID', peso: 38,  valor: r => r.metaIdMensagem ?? '—' },
    { head: 'STATUS', peso: 5,   center: true, valor: r => this.traduzirStatusPdf(r.statusMensagem) },
    { head: 'DATE', peso: 8.75,   center: true, valor: r => this.formatarData(r.dataEnvioMensagem) },
    { head: 'RECEBIMENTO', peso: 8.75,   center: true, valor: r => this.formatarData(r.dataRecebimentoMensagem) },
  ];

  private statusIcones: Record<string, { data: string; ratio: number }> = {};

  usuarioExport = ""

  private readonly service = inject(RelatorioDisparoService);
  private readonly dialogRef = inject(MatDialogRef<RelatorioDisparoComponent>);
  private readonly authService = inject(AuthService);
  private readonly msgService = inject(MsgService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    const usuarioLogado: UsuarioLogado = this.authService.getUsuarioLogadoClariBot();
    this.usuarioExport = usuarioLogado.nome ?? ""
    this.carregar(this.dialogData.disparoId);
  }

  carregar(disparoId: number): void {
    this.carregando.set(true);

    this.service.buscarRelatorioPorDisparoId(disparoId).subscribe({
      next: (data) => {
        this.relatorio.set(data);
        this.dataExport.set(new Date());
        this.carregando.set(false);
      },
      error: () => {
        this.msgService.msgErro('Erro ao carregar relatório.');
        this.carregando.set(false);
      }
    });
  }

  async exportarPdf(): Promise<void> {
    const rel = this.relatorio();
    if (!rel) return;

    this.progresso.set(null);
    this.exportando.set(true);
    this.cdr.detectChanges();
    await this.cederThread();

    try {
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      const { registrarMontserrat } = await import('./montserrat-fonts');
      registrarMontserrat(pdf);
      pdf.setFont('Montserrat', 'normal');

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const MARGIN = 10;
      const TABELA_Y = 16;
      const BOTTOM_Y = pageH - 10;
      const FOOTER_Y = pageH - 8;
      const HEADER_H = 5.6;
      const ROW_H = 5.5;
      const tableWidth = pageW - 2 * MARGIN;

      await this.aguardarFontes();
      const logo = await this.carregarLogo('assets/logo-nova.svg');
      this.statusIcones = await this.prepararIconesStatus();
      const painel = await this.capturarPainel();

      const linhas = this.comprovacao();
      const itensP1Real = Math.min(this.ITENS_P1, linhas.length);
      const restante = Math.max(0, linhas.length - itensP1Real);
      const totalPaginas = Math.max(1, 1 + (restante > 0 ? Math.ceil(restante / this.ITENS_P2) : 0));

      const head = [this.colunasPdf.map(c => c.head)];
      const somaPesos = this.colunasPdf.reduce((s, c) => s + c.peso, 0);
      const columnStyles: Record<number, { cellWidth: number; halign: 'center' | 'left' }> = {};
      this.colunasPdf.forEach((c, i) => (columnStyles[i] = {
        cellWidth: (c.peso / somaPesos) * tableWidth,
        halign: c.center ? 'center' : 'left',
      }));

      const GAP_INFO_TABELA = 5.3;
      const TITULO_COMPROV_H = 10;
      let painelW = tableWidth;
      let painelH = painel ? (painel.rawH * painelW) / painel.rawW : 0;

      const tabelaH_P1 = HEADER_H + itensP1Real * ROW_H;
      const maxPainelH = BOTTOM_Y - TABELA_Y - GAP_INFO_TABELA - TITULO_COMPROV_H - tabelaH_P1;
      if (painel && maxPainelH > 20 && painelH > maxPainelH) {
        painelH = maxPainelH;
        painelW = (painel.rawW * painelH) / painel.rawH;
      }

      this.progresso.set({ atual: 1, total: totalPaginas });
      await this.cederThread();

      this.desenharTitulo(pdf, logo, MARGIN);

      let yTituloComprov = TABELA_Y + 4;
      if (painel) {
        pdf.addImage(painel.data, 'JPEG', MARGIN, TABELA_Y, painelW, painelH);
        yTituloComprov = TABELA_Y + painelH + GAP_INFO_TABELA + 4;
      }
      const yTabela1 = this.desenharTituloComprovacoes(pdf, MARGIN, yTituloComprov, tableWidth * 0.4);

      const chunk1 = linhas.slice(0, itensP1Real);
      this.desenharTabelaPdf(pdf, chunk1, head, columnStyles, tableWidth, yTabela1, MARGIN, BOTTOM_Y);
      this.desenharRodape(pdf, MARGIN, pageW, FOOTER_Y, 1, totalPaginas, rel.dadosDisparo?.uuid);

      let offset = itensP1Real;
      let pagina = 2;
      while (offset < linhas.length) {
        this.progresso.set({ atual: pagina, total: totalPaginas });
        await this.cederThread();

        pdf.addPage();
        this.desenharTitulo(pdf, logo, MARGIN);
        const yTabela = this.desenharTituloComprovacoes(pdf, MARGIN, TABELA_Y + 6, tableWidth * 0.4);
        const chunk = linhas.slice(offset, offset + this.ITENS_P2);
        this.desenharTabelaPdf(pdf, chunk, head, columnStyles, tableWidth, yTabela, MARGIN, BOTTOM_Y);
        this.desenharRodape(pdf, MARGIN, pageW, FOOTER_Y, pagina, totalPaginas, rel.dadosDisparo?.uuid);

        offset += this.ITENS_P2;
        pagina++;
      }

      pdf.save(`relatorio-disparo-${rel.dadosDisparo?.idDisparo ?? 'export'}.pdf`);
    } catch (e) {
      console.error('Falha ao gerar PDF do relatório de disparo:', e);
      this.msgService.msgErro('Erro ao gerar PDF.');
    } finally {
      this.exportando.set(false);
      this.progresso.set(null);
    }
  }

  fechar(): void {
    this.dialogRef.close();
  }

  traduzirStatus(status?: string): string {
    return status ? status.toUpperCase() : '—';
  }

  formatarCpf(valor?: string): string {
    if (!valor) return '—';
    const digitos = String(valor).replace(/\D/g, '').length;
    return digitos === 11 || digitos === 14 ? this.cpfMask.transform(valor) : '—';
  }

  formatarTelefone(valor?: string | number | null): string {
    if (valor === null || valor === undefined || valor === '') return '—';
    return this.telMask.transform(valor) || '—';
  }

  trackByIndex(index: number): number {
    return index;
  }

  private traduzirStatusPdf(status?: string): string {
    const s = (status ?? '').toUpperCase();
    switch (s) {
      case 'SENT':         return 'SENT';
      case 'DELIVERED':    return 'DELIV';
      case 'READ':         return 'READ';
      case 'FAILED':       return 'FAIL';
      case 'POSSUE_SESSAO': return 'SESS';
      default:             return s || '—';
    }
  }

  private cederThread(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  private formatarData(data?: Date | string): string {
    if (!data) return '—';
    const d = data instanceof Date ? data : new Date(data);
    if (isNaN(d.getTime())) return '—';
    const p = (n: number) => `${n}`.padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} - ${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  private async capturarPainel(): Promise<{ data: string; rawW: number; rawH: number } | null> {
    const el = this.painelRef()?.nativeElement;
    if (!el) return null;
    const CAPTURE_W = 1200;
    const prev = {
      width: el.style.width,
      minWidth: el.style.minWidth,
      maxWidth: el.style.maxWidth,
      flex: el.style.flex,
    };
    el.style.flex = `0 0 ${CAPTURE_W}px`;
    el.style.width = `${CAPTURE_W}px`;
    el.style.minWidth = `${CAPTURE_W}px`;
    el.style.maxWidth = `${CAPTURE_W}px`;
    void el.offsetWidth;

    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        removeContainer: true,
        width: CAPTURE_W,
        windowWidth: CAPTURE_W,
        ignoreElements: (node) => node.tagName?.toLowerCase() === 'app-root',
      });
      return {
        data: canvas.toDataURL('image/jpeg', 0.92),
        rawW: canvas.width,
        rawH: canvas.height,
      };
    } finally {
      el.style.width = prev.width;
      el.style.minWidth = prev.minWidth;
      el.style.maxWidth = prev.maxWidth;
      el.style.flex = prev.flex;
    }
  }

  private async prepararIconesStatus(): Promise<Record<string, { data: string; ratio: number }>> {
    const icones: Record<string, { data: string; ratio: number }> = {};
    try {
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      if (fonts) {
        await fonts.load('64px "Material Icons"');
        await fonts.ready;
      }
    } catch {
      // ignora falha ao carregar fonte de ícones
    }

    const CHECK = String.fromCharCode(0xe5ca);
    const DONE_ALL = String.fromCharCode(0xe877);
    const ERROR = String.fromCharCode(0xe000);

    icones['SENT'] = this.renderIconeGlifo(CHECK, '#2b2b2b');
    icones['DELIVERED'] = this.renderIconeGlifo(DONE_ALL, '#2b2b2b');
    icones['READ'] = this.renderIconeGlifo(DONE_ALL, '#00bfff');
    icones['FAILED'] = this.renderIconeGlifo(ERROR, '#e53935');

    const sessao = await this.carregarLogo('assets/icones/conato-possue-sessao.svg');
    if (sessao) icones['POSSUE_SESSAO'] = sessao;

    return icones;
  }

  private renderIconeGlifo(glifo: string, cor: string): { data: string; ratio: number } {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = cor;
    ctx.font = `${size}px "Material Icons"`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(glifo, size / 2, size / 2 + 2);
    return { data: canvas.toDataURL('image/png'), ratio: 1 };
  }

  private desenharTabelaPdf(pdf: jsPDF, chunk: RelatorioComprovacaoEnvio[], head: string[][], columnStyles: Record<number, { cellWidth: number; halign: 'center' | 'left' }>, tableWidth: number, startY: number, margin: number, bottomY: number): void {
    const clipH = bottomY - startY;
    if (clipH <= 0) return;

    const RAIO = 2.2;
    pdf.saveGraphicsState();
    (pdf as any).roundedRect(margin, startY, tableWidth, clipH, RAIO, RAIO, null);
    (pdf as any).clip();
    (pdf as any).discardPath();

    const statusIdx = this.colunasPdf.findIndex(c => c.head === 'STATUS');

    autoTable(pdf, {
      head,
      body: chunk.map(r => this.colunasPdf.map(c => c.valor(r))),
      startY,
      margin: { left: margin, right: margin },
      theme: 'plain',
      tableWidth,
      columnStyles,
      styles: {
        font: 'Montserrat', fontStyle: 'normal',
        fontSize: 6.5, cellPadding: 1.6, overflow: 'ellipsize',
        textColor: GRAFITE, lineWidth: 0,
      },
      headStyles: {
        font: 'Montserrat', fontStyle: 'bold',
        fillColor: ROXO, textColor: [255, 255, 255],
        fontSize: 6.5,
      },
      didParseCell: (data) => {
        const colConfig = this.colunasPdf[data.column.index];
        data.cell.styles.halign = colConfig?.center ? 'center' : 'left';

        if (data.section === 'body' && data.column.index === statusIdx) {
          const row = chunk[data.row.index];
          const s = (row?.statusMensagem ?? '').toUpperCase();
          if (this.statusIcones[s]) data.cell.text = [''];
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          const yLinha = data.cell.y + data.cell.height;
          pdf.setDrawColor(232, 232, 236);
          pdf.setLineWidth(0.1);
          pdf.line(margin, yLinha, margin + tableWidth, yLinha);
        }

        if (data.section === 'body' && data.column.index === statusIdx) {
          const row = chunk[data.row.index];
          const s = (row?.statusMensagem ?? '').toUpperCase();
          const icone = this.statusIcones[s];
          if (icone) {
            const h = 3.0;
            const w = h * (icone.ratio || 1);
            const cx = data.cell.x + data.cell.width / 2;
            const cy = data.cell.y + data.cell.height / 2;
            try { pdf.addImage(icone.data, 'PNG', cx - w / 2, cy - h / 2, w, h); } catch {
              // ignora falha ao desenhar ícone de status
            }
          }
        }
      },
    });

    pdf.restoreGraphicsState();
  }

  private desenharTitulo(pdf: jsPDF, logo: { data: string; ratio: number } | null, margin: number): void {
    let textX = margin;
    if (logo) {
      const altura = 9;
      const largura = altura * logo.ratio;
      try {
        pdf.addImage(logo.data, 'PNG', margin, 4, largura, altura);
        textX = margin + largura + 3;
      } catch {
        // ignora falha ao desenhar logo
      }
    }
    pdf.setFont('Montserrat', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(...ROXO);
    pdf.text('RELATÓRIO DE DISPARO', textX, 12);
  }

  private desenharTituloComprovacoes(pdf: jsPDF, x: number, y: number, larguraDivisor: number): number {
    pdf.setFont('Montserrat', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...ROXO);
    pdf.text('COMPROVAÇÕES DE ENVIO', x, y);
    this.desenharDivisorGradiente(pdf, x, y + 2, larguraDivisor);
    return y + 6;
  }

  private desenharRodape(pdf: jsPDF, margin: number, pageW: number, y: number, pagina: number, total: number, uuid?: string): void {
    pdf.setFont('Montserrat', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...CINZA);
    const data = this.formatarData(this.dataExport());
    pdf.text(this.usuarioExport ? `${data} - ${this.usuarioExport}` : data, margin, y);
    if (uuid){
      pdf.text(('disparoId: ' + uuid), pageW / 2, y, { align: 'center' });
    }
    pdf.text(`${pagina} / ${total}`, pageW - margin, y, { align: 'right' });
  }

  private desenharDivisorGradiente(pdf: jsPDF, x: number, y: number, largura: number): void {
    const segmentos = 1000;
    const passo = largura / segmentos;
    const GState = (pdf as any).GState;
    for (let i = 0; i < segmentos; i++) {
      (pdf as any).setGState(new GState({ opacity: 1 - i / segmentos }));
      pdf.setFillColor(ROXO[0], ROXO[1], ROXO[2]);
      pdf.rect(x + i * passo, y, passo + 0.3, 0.7, 'F');
    }
    (pdf as any).setGState(new GState({ opacity: 1 }));
  }

  private async aguardarFontes(): Promise<void> {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts) return;
    try {
      await Promise.all([
        fonts.load('500 12px Montserrat'),
        fonts.load('600 12px Montserrat'),
        fonts.load('700 12px Montserrat'),
      ]);
      await fonts.ready;
    } catch {
      // ignora falha ao carregar fontes
    }
  }

  private carregarLogo(url: string): Promise<{ data: string; ratio: number } | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const nw = img.naturalWidth || 100;
        const nh = img.naturalHeight || 100;
        const escala = 3;
        const canvas = document.createElement('canvas');
        canvas.width = nw * escala;
        canvas.height = nh * escala;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        try {
          resolve({ data: canvas.toDataURL('image/png'), ratio: nw / nh });
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }
}
